import { parse as parseYaml } from 'yaml';
/**
 * Whether a Learn workspace file parses as YAML: null when it does, else the
 * parser's message. The server refuses to save a file that fails this, and
 * the workspace page applies the same rule before it asks, so the learner
 * hears about a mistake where they made it.
 */
export const validateLearnWorkspaceYaml = (content) => {
    try {
        parseYaml(content);
        return null;
    }
    catch (e) {
        return e instanceof Error ? e.message : 'Invalid YAML';
    }
};
/**
 * One line for the learner: the parser's messages run to several lines with
 * a snippet, and name the line, which is all the page needs to point at.
 */
export const describeLearnWorkspaceYamlError = (message) => {
    const line = /at line (\d+)/.exec(message)?.[1];
    return line
        ? `Fix the YAML error on line ${line} to continue`
        : 'Fix the YAML error to continue';
};
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const namedItems = (value) => Array.isArray(value) ? value.filter(isRecord) : [];
/** dbt reads a column's meta from `meta` and, from 1.10, `config.meta`. */
const metaBlocks = (column) => [
    column.meta,
    isRecord(column.config) ? column.config.meta : undefined,
];
const columnHolds = (column, under, field) => metaBlocks(column).some((meta) => isRecord(meta) && isRecord(meta[under]) && field in meta[under]);
/**
 * Null when the file holds what the lesson expects; otherwise one line for
 * the learner saying what is missing or where it ended up instead.
 */
export const checkLearnLessonEntry = (content, expected) => {
    let doc;
    try {
        doc = parseYaml(content);
    }
    catch (e) {
        return describeLearnWorkspaceYamlError(e instanceof Error ? e.message : 'Invalid YAML').replace(/ to continue$/, ', then check again');
    }
    const models = namedItems(isRecord(doc) ? doc.models : undefined);
    const model = models.find((m) => m.name === expected.model);
    if (!model)
        return `The ${expected.model} model is missing from this file`;
    const columns = namedItems(model.columns);
    if (expected.column === undefined) {
        if (columns.some((c) => c.name === expected.field))
            return null;
        const elsewhere = models.find((m) => m !== model &&
            namedItems(m.columns).some((c) => c.name === expected.field));
        if (elsewhere) {
            return `${expected.field} is under the ${String(elsewhere.name)} model. Add it under ${expected.model}`;
        }
        return `Add ${expected.field} to the ${expected.model} model's ${expected.under}`;
    }
    const column = columns.find((c) => c.name === expected.column);
    if (!column) {
        return `The ${expected.column} column is missing from ${expected.model}`;
    }
    if (columnHolds(column, expected.under, expected.field))
        return null;
    const otherColumn = models
        .flatMap((m) => namedItems(m.columns))
        .find((c) => c !== column && columnHolds(c, expected.under, expected.field));
    if (otherColumn) {
        return `${expected.field} is under the ${String(otherColumn.name)} column. Add it under ${expected.column}`;
    }
    return `Add ${expected.field} under the ${expected.column} column's ${expected.under}`;
};
export const isContentAsCodeExpectation = (expect) => expect !== undefined &&
    expect.kind === 'contentAsCode' &&
    typeof expect.slug === 'string' &&
    typeof expect.name === 'string';
/**
 * Null when the chart file holds what the lesson expects; otherwise one line
 * for the learner saying what to put right.
 */
export const checkContentAsCodeEntry = (content, expected) => {
    let doc;
    try {
        doc = parseYaml(content);
    }
    catch (e) {
        return describeLearnWorkspaceYamlError(e instanceof Error ? e.message : 'Invalid YAML').replace(/ to continue$/, ', then check again');
    }
    if (!isRecord(doc))
        return 'This file no longer describes a chart';
    if (doc.slug !== expected.slug) {
        return `Keep the slug as ${expected.slug}: upload finds the chart by it`;
    }
    if (doc.name !== expected.name) {
        return `Change the chart's name to ${expected.name}`;
    }
    return null;
};
//# sourceMappingURL=learnSandbox.js.map